import type { CSSProperties, ReactNode } from 'react';
import { jiraIssueUrl, useJiraHost } from '../lib/jiraLinks';

interface Props {
  /** Jira issue key (e.g. "TEXEC-1842", "KEN-114"). */
  keyId: string;
  /** Existing class names; usually `badge mono small` to match surrounding pills. */
  className?: string;
  /** Optional override for the rendered label (defaults to the key). */
  children?: ReactNode;
  /** Optional inline style overrides. */
  style?: CSSProperties;
  /** Optional title override; defaults to `Open <key> in Jira`. */
  title?: string;
}

/**
 * Renders a Jira issue key as an external link to `<host>/browse/<key>` when
 * the Jira host is configured. Falls back to a plain styled span (keeping the
 * same class names) so layout doesn't shift if Jira isn't set up.
 */
export function JiraKeyLink({ keyId, className, children, style, title }: Props) {
  const host = useJiraHost();
  const url = jiraIssueUrl(host, keyId);
  const label = children ?? keyId;
  if (!url) {
    return (
      <span className={className} style={style} title={title}>
        {label}
      </span>
    );
  }
  return (
    <a
      className={`${className ?? ''} jira-link`.trim()}
      href={url}
      style={style}
      target="_blank"
      rel="noreferrer"
      title={title ?? `Open ${keyId} in Jira`}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </a>
  );
}
